import { useNavigate, useParams } from "@tanstack/solid-router";
import { createEffect, createSignal, For, Show } from "solid-js";
import { SeatFieldsForm, seatFieldsFrom } from "~/components/SeatFieldsForm";
import {
  ApiError,
  fetchMyOrder,
  resendMyOrderSeat,
  updateMyOrderSeat,
  type BuyerOrder,
  type SeatFields,
} from "~/lib/api";
import { cityLabel } from "~/lib/tickets";

export function ManageOrderPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const orderId = () => params().orderId ?? "";

  const [order, setOrder] = createSignal<BuyerOrder | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);
  const [activeSeat, setActiveSeat] = createSignal<string | null>(null);
  const [fields, setFields] = createSignal<SeatFields>(seatFieldsFrom());
  const [saving, setSaving] = createSignal(false);
  const [formError, setFormError] = createSignal<string | null>(null);
  const [notice, setNotice] = createSignal<string | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMyOrder(orderId());
      setOrder(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load order");
    } finally {
      setLoading(false);
    }
  }

  createEffect(() => {
    if (orderId()) void reload();
  });

  function editSeat(attendeeId: string) {
    const seat = order()?.seats.find((s) => s.attendee_id === attendeeId);
    if (!seat) return;
    setActiveSeat(attendeeId);
    setFields(seatFieldsFrom(seat.fields));
    setFormError(null);
    setNotice(null);
  }

  async function onSave() {
    const seatId = activeSeat();
    if (!seatId) return;
    setSaving(true);
    setFormError(null);
    try {
      await updateMyOrderSeat(orderId(), seatId, fields());
      setNotice("Seat saved");
      await reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onResend(attendeeId: string) {
    setNotice(null);
    try {
      const res = await resendMyOrderSeat(orderId(), attendeeId);
      setNotice(`Invite sent to ${res.data.to_email}`);
      await reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Resend failed");
    }
  }

  return (
    <section class="card">
      <button type="button" class="btn btn-outline" onClick={() => navigate({ to: "/tickets" })}>
        ← All tickets
      </button>
      <Show when={loading()}>
        <p class="muted">Loading…</p>
      </Show>
      <Show when={error()}>
        <div class="error-box">{error()}</div>
      </Show>
      <Show when={!loading() && order()}>
        {(o) => (
          <>
            <h1 style={{ "margin-top": "16px" }}>{o().event_name}</h1>
            <p class="muted">
              {cityLabel(o().event_id)}
              <Show when={o().event_date_label}> · {o().event_date_label}</Show>
              {" · "}
              {o().status}
            </p>

            <Show when={notice()}>
              <p class="status-chip">{notice()}</p>
            </Show>

            <h2>Seats</h2>
            <ul class="event-list">
              <For each={o().seats}>
                {(seat) => (
                  <li class="card" style={{ padding: "12px", "margin-bottom": "8px" }}>
                    <div>
                      <strong>Seat {seat.slot_index}</strong>
                      <span class="muted">
                        {" "}
                        — {seat.assigned ? "Named" : "Needs name"}
                      </span>
                    </div>
                    <Show when={seat.fields.email}>
                      <div class="muted">{seat.fields.email}</div>
                    </Show>
                    <div style={{ display: "flex", gap: "8px", "margin-top": "8px", "flex-wrap": "wrap" }}>
                      <button type="button" class="btn btn-outline" onClick={() => editSeat(seat.attendee_id)}>
                        Edit
                      </button>
                      <Show when={seat.assign_url}>
                        <button
                          type="button"
                          class="btn btn-outline"
                          onClick={() => onResend(seat.attendee_id)}
                        >
                          Resend assign link
                        </button>
                      </Show>
                      <Show when={o().status !== "refunded"}>
                        <a
                          class="btn btn-outline"
                          href={`/onboarding/${o().event_id}`}
                        >
                          Claim in Connect
                        </a>
                      </Show>
                    </div>
                  </li>
                )}
              </For>
            </ul>

            <Show when={activeSeat()}>
              <h2>Edit seat</h2>
              <SeatFieldsForm
                value={fields()}
                onChange={setFields}
                onSubmit={onSave}
                saving={saving()}
                error={formError()}
                submitLabel="Save seat"
              />
            </Show>
          </>
        )}
      </Show>
    </section>
  );
}
